import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, SearchField } from '@factorialco/deprecated-design-system'
import { OpenPositionFragment, OpenPositionsDocument } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import { useOpenPositionsEnabled } from 'modules/ats/lib/atsEnabled'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { EmptyListView } from './components/EmptyListView'
import { OpenPositionsList } from './components/OpenPositionsList'
import { useOpenPositionsGroups } from './useOpenPositionsGroup'

const OpenPositionsPage = () => {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const openPositionsEnabled = useOpenPositionsEnabled()
  const navigate = useNavigate()

  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const [filterByLocation, setLocationFilter] = useState<number | ''>('')
  const [filterByTeam, setTeamFilter] = useState<number | ''>('')
  const [filterByWorkplaceType, setWorkplaceTypeFilter] = useState<string | ''>('')

  const { data, loading } = useQuery(OpenPositionsDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const atsCompanyId = data?.ats.companiesConnection.nodes[0]?.companyId

  const isCompanyPageInstalled =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_careers_page')
      ?.enabled ?? false

  const isCompanyPageConfigured = !!company.slug && !!company.countryFactorialDomain

  const companyPageReady = isCompanyPageInstalled && isCompanyPageConfigured

  // TODO: Move this check to the route loader
  useEffect(() => {
    if (!openPositionsEnabled) {
      navigate(navigation.ats.fullPath())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsCompanyId])

  const openPositions = data?.ats.openPositions ?? []

  const openPositionsToShow = useMemo(() => {
    return openPositions.filter((job) => {
      const matchesSearch =
        matchText(debouncedSearch, job.title) ||
        matchText(debouncedSearch, job.team?.name || '') ||
        matchText(debouncedSearch, job.location?.city || '')

      const matchesLocation = filterByLocation ? job.location?.id === filterByLocation : true
      const matchesTeam = filterByTeam ? job.team?.id === filterByTeam : true
      const matchesWorkplaceType = filterByWorkplaceType
        ? job.workplaceType === filterByWorkplaceType
        : true

      return matchesSearch && matchesLocation && matchesTeam && matchesWorkplaceType
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterByLocation, filterByTeam, filterByWorkplaceType, openPositions.length])

  const openPositionsGroups = useOpenPositionsGroups(
    orderBy(
      openPositionsToShow,
      (openPosition: OpenPositionFragment) => openPosition.location?.city
    )
  )

  const locationOptions = [
    ...openPositions
      .reduce((map, obj) => {
        const location = obj.location
        if (location?.city && !map.has(location.id)) {
          map.set(location.id, { value: location.id, label: location.city ?? '' })
        }
        return map
      }, new Map())
      .values(),
  ]

  const teamOptions = [
    ...new Map(
      openPositions
        .map((obj) => obj.team)
        .filter((team) => team && typeof team === 'object' && team.id !== undefined && team.name)
        .map((team) => [team?.id, { value: team?.id, label: team?.name ?? '' }])
    ).values(),
  ]

  const locationsGroups = Object.entries(openPositionsGroups)
    .filter(([key]) => key !== 'noLocation')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

  return (
    <ResponsivePage.Body loading={loading} variant="wide" error={null}>
      <Box justifyContent="spaceBetween" flexDirection="row" gap="s16" width="full">
        {!!locationOptions.length && (
          <Select
            none={i18n.t('ats.open_positions.filters.location_label')}
            value={filterByLocation.toString()}
            onChange={(value) => setLocationFilter(Number(value))}
            options={locationOptions}
          />
        )}
        <Select
          none={i18n.t('ats.open_positions.filters.job_type_label')}
          value={filterByWorkplaceType}
          onChange={(value) => setWorkplaceTypeFilter(value)}
          options={[
            {
              label: i18n.t('ats.workplace_types.onsite'),
              value: 'onsite',
            },
            {
              label: i18n.t('ats.workplace_types.hybrid'),
              value: 'hybrid',
            },
            {
              label: i18n.t('ats.workplace_types.remote'),
              value: 'remote',
            },
          ]}
        />
        <Select
          none={i18n.t('ats.open_positions.filters.team_label')}
          value={filterByTeam.toString()}
          onChange={(value) => setTeamFilter(Number(value))}
          options={teamOptions}
        />
        <Box minWidth="s400">
          <SearchField
            variant="big"
            search={search}
            onSearch={onSearch}
            placeholder={i18n.t('common.search_by')}
          />
        </Box>
      </Box>

      <Box flexGrow gap="s40" paddingTop="s40">
        {Object.keys(openPositionsGroups).length < 1 ? (
          <EmptyListView
            canCreateJobPostings={data?.ats._policies.createJobPostings.can ?? false}
            noOpenPositions={openPositions.length < 1}
          />
        ) : (
          <>
            {Object.keys(locationsGroups).map((location: string) => {
              return (
                <OpenPositionsList
                  title={location}
                  openPositions={openPositionsGroups[location] || []}
                  companyPageReady={companyPageReady}
                  key={location}
                />
              )
            })}
            {!!openPositionsGroups['noLocation'] && (
              <OpenPositionsList
                title={i18n.t('ats.open_positions.no_location')}
                openPositions={openPositionsGroups['noLocation']}
                companyPageReady={companyPageReady}
              />
            )}
          </>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default OpenPositionsPage
